import { BrowserQRCodeReader, IScannerControls } from "@zxing/browser";
import QrScanner from "qr-scanner";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { OnResultFunction, QrReaderProps } from "react-qr-reader";

const QR_CODE_FORMAT = "qr_code";

type DetectedBarcode = {
  rawValue?: string;
  format?: string;
};

type NativeBarcodeDetector = {
  detect: (source: HTMLVideoElement) => Promise<DetectedBarcode[]>;
};

type NativeBarcodeDetectorConstructor = {
  new (options?: { formats?: string[] }): NativeBarcodeDetector;
  getSupportedFormats?: () => Promise<string[]>;
};

type WindowWithBarcodeDetector = Window & {
  BarcodeDetector?: NativeBarcodeDetectorConstructor;
};

type QrScanResult = NonNullable<Parameters<OnResultFunction>[0]>;
type FastQrEngine = "native" | "qr-scanner" | "zxing";

type FastQrReaderProps = {
  constraints?: MediaTrackConstraints;
  onResult?: OnResultFunction;
  ViewFinder?: QrReaderProps["ViewFinder"];
  duplicateScanDelay?: number;
  scanDelay?: number;
  nativeScanDelay?: number;
  scanSuccessDelay?: number;
  workerRequiredSuccessfulScans?: number;
  videoId?: string;
  className?: string;
  containerStyle?: React.CSSProperties;
  videoContainerStyle?: React.CSSProperties;
  videoStyle?: React.CSSProperties;
};

export const FAST_QR_NATIVE_SCAN_DELAY_MS = 25;
export const FAST_QR_NATIVE_SCAN_SUCCESS_DELAY_MS = 25;
export const FAST_QR_WORKER_SCAN_DELAY_MS = 40;
export const FAST_QR_WORKER_HIGH_RES_SCAN_INTERVAL = 4;
export const FAST_QR_WORKER_FULL_FRAME_WIDTH = 960;
export const FAST_QR_WORKER_HIGH_RES_FULL_FRAME_WIDTH = 1280;
export const FAST_QR_WORKER_REQUIRED_SUCCESSFUL_SCANS = 2;
export const FAST_QR_ZXING_SCAN_DELAY_MS = 100;
export const FAST_QR_DUPLICATE_SCAN_SUPPRESSION_MS = 1500;
export const FAST_QR_CAMERA_IDLE_RELEASE_MS = 60000;

export const FAST_QR_SCAN_DELAY_MS = FAST_QR_WORKER_SCAN_DELAY_MS;
export const FAST_QR_SCAN_SUCCESS_DELAY_MS =
  FAST_QR_NATIVE_SCAN_SUCCESS_DELAY_MS;

export const FAST_QR_CAMERA_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: "environment" },
  width: { ideal: 1920 },
  resizeMode: { ideal: "none" },
  frameRate: { ideal: 30, max: 30 },
};

const isDevelopment = process.env.NODE_ENV === "development";

let retainedCameraStream: MediaStream | null = null;
let retainedCameraStreamPromise: Promise<MediaStream> | null = null;
let retainedCameraConstraintsKey: string | null = null;
let retainedCameraSessionCount = 0;
let retainedCameraReleaseTimeoutId: number | undefined;

const getTimestamp = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const formatQrValueForLog = (value: string) =>
  value.length > 120 ? `${value.slice(0, 120)}...` : value;

const debugQrScanner = (message: string, data?: Record<string, unknown>) => {
  if (!isDevelopment) return;

  if (data) {
    console.debug(`[FastQrReader] ${message}`, data);
  } else {
    console.debug(`[FastQrReader] ${message}`);
  }
};

const getCameraConstraintsKey = (constraints: MediaTrackConstraints) =>
  JSON.stringify(constraints);

const clearRetainedCameraReleaseTimeout = () => {
  window.clearTimeout(retainedCameraReleaseTimeoutId);
  retainedCameraReleaseTimeoutId = undefined;
};

const isCameraStreamLive = (stream?: MediaStream | null) =>
  !!stream &&
  stream.getVideoTracks().some((track) => track.readyState === "live");

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

const stopRetainedCameraStream = () => {
  retainedCameraStream?.getTracks().forEach((track) => track.stop());
  retainedCameraStream = null;
  retainedCameraStreamPromise = null;
  retainedCameraConstraintsKey = null;
};

const acquireRetainedCameraStream = async (
  constraints: MediaTrackConstraints
) => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("MediaDevices API is not available");
  }

  const constraintsKey = getCameraConstraintsKey(constraints);
  retainedCameraSessionCount += 1;
  clearRetainedCameraReleaseTimeout();

  if (
    retainedCameraConstraintsKey === constraintsKey &&
    isCameraStreamLive(retainedCameraStream)
  ) {
    debugQrScanner("camera:reuse", {
      sessionCount: retainedCameraSessionCount,
      settings: retainedCameraStream?.getVideoTracks()[0]?.getSettings(),
    });
    return retainedCameraStream as MediaStream;
  }

  if (
    retainedCameraConstraintsKey === constraintsKey &&
    retainedCameraStreamPromise
  ) {
    debugQrScanner("camera:reuse-pending", {
      sessionCount: retainedCameraSessionCount,
    });
    return retainedCameraStreamPromise;
  }

  if (retainedCameraStream || retainedCameraStreamPromise) {
    stopRetainedCameraStream();
  }

  retainedCameraConstraintsKey = constraintsKey;
  debugQrScanner("camera:open", { constraints });
  retainedCameraStreamPromise = navigator.mediaDevices
    .getUserMedia({ video: constraints })
    .then((stream) => {
      retainedCameraStream = stream;
      retainedCameraStreamPromise = null;
      debugQrScanner("camera:stream-settings", {
        settings: stream.getVideoTracks()[0]?.getSettings(),
      });
      return stream;
    })
    .catch((error) => {
      retainedCameraStreamPromise = null;
      retainedCameraConstraintsKey = null;
      retainedCameraSessionCount = Math.max(0, retainedCameraSessionCount - 1);
      throw error;
    });

  return retainedCameraStreamPromise;
};

const releaseRetainedCameraStream = () => {
  retainedCameraSessionCount = Math.max(0, retainedCameraSessionCount - 1);

  if (retainedCameraSessionCount > 0) return;

  clearRetainedCameraReleaseTimeout();
  retainedCameraReleaseTimeoutId = window.setTimeout(() => {
    if (retainedCameraSessionCount > 0) return;

    debugQrScanner("camera:release-idle", {
      idleMs: FAST_QR_CAMERA_IDLE_RELEASE_MS,
    });
    stopRetainedCameraStream();
  }, FAST_QR_CAMERA_IDLE_RELEASE_MS);

  debugQrScanner("camera:release-scheduled", {
    idleMs: FAST_QR_CAMERA_IDLE_RELEASE_MS,
  });
};

const createRetainedCameraSession = (constraints: MediaTrackConstraints) => {
  let hasActiveSession = false;
  let isAcquirePending = false;
  let shouldReleaseAfterAcquire = false;

  const acquire = async () => {
    isAcquirePending = true;
    shouldReleaseAfterAcquire = false;

    try {
      const stream = await acquireRetainedCameraStream(constraints);
      isAcquirePending = false;

      if (shouldReleaseAfterAcquire) {
        shouldReleaseAfterAcquire = false;
        releaseRetainedCameraStream();
        return null;
      }

      hasActiveSession = true;
      return stream;
    } catch (error) {
      isAcquirePending = false;
      shouldReleaseAfterAcquire = false;
      throw error;
    }
  };

  const release = () => {
    if (hasActiveSession) {
      hasActiveSession = false;
      releaseRetainedCameraStream();
      return;
    }

    if (isAcquirePending) {
      shouldReleaseAfterAcquire = true;
    }
  };

  return { acquire, release };
};

const defaultVideoContainerStyle: React.CSSProperties = {
  width: "100%",
  paddingTop: "100%",
  overflow: "hidden",
  position: "relative",
};

const defaultVideoStyle: React.CSSProperties = {
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "block",
  overflow: "hidden",
  position: "absolute",
};

const getBarcodeDetector = () => {
  if (typeof window === "undefined") return undefined;
  return (window as WindowWithBarcodeDetector).BarcodeDetector;
};

const getFacingMode = (constraints?: MediaTrackConstraints) => {
  const facingMode = constraints?.facingMode;

  if (typeof facingMode === "string") return facingMode;
  if (!facingMode || typeof facingMode !== "object") return undefined;

  const ideal = (facingMode as ConstrainDOMStringParameters).ideal;
  if (typeof ideal === "string") return ideal;
  if (Array.isArray(ideal)) return ideal[0];

  return undefined;
};

const createNativeQrResult = (rawValue: string) =>
  ({
    getText: () => rawValue,
    text: rawValue,
  } as unknown as QrScanResult);

const useDuplicateSuppressedOnResult = ({
  activeEngine,
  duplicateScanDelay,
  onResult,
}: {
  activeEngine: FastQrEngine;
  duplicateScanDelay: number;
  onResult?: OnResultFunction;
}) => {
  const lastEmittedAtRef = useRef(0);
  const lastEmittedValueRef = useRef("");

  useEffect(() => {
    lastEmittedAtRef.current = 0;
    lastEmittedValueRef.current = "";
  }, [activeEngine]);

  return useCallback<OnResultFunction>(
    (result, error, codeReader) => {
      if (!result) {
        onResult?.(result, error, codeReader);
        return;
      }

      const value = result.getText();
      const now = getTimestamp();

      if (
        value === lastEmittedValueRef.current &&
        now - lastEmittedAtRef.current < duplicateScanDelay
      ) {
        debugQrScanner("result:duplicate-suppressed", {
          duplicateScanDelay,
          engine: activeEngine,
          value: formatQrValueForLog(value),
        });
        return;
      }

      lastEmittedAtRef.current = now;
      lastEmittedValueRef.current = value;
      onResult?.(result, error, codeReader);
    },
    [activeEngine, duplicateScanDelay, onResult]
  );
};

const waitForVideoReady = async (video: HTMLVideoElement) => {
  if (
    video.readyState >= HTMLMediaElement.HAVE_METADATA &&
    video.videoWidth &&
    video.videoHeight
  ) {
    return;
  }

  await new Promise<void>((resolve) => {
    let timeoutId = 0;

    const finish = () => {
      window.clearTimeout(timeoutId);
      video.removeEventListener("loadedmetadata", finish);
      video.removeEventListener("canplay", finish);
      resolve();
    };

    timeoutId = window.setTimeout(finish, 5000);
    video.addEventListener("loadedmetadata", finish);
    video.addEventListener("canplay", finish);
  });
};

const getQrScannerScanRegion = (
  video: HTMLVideoElement,
  attemptCount: number
): QrScanner.ScanRegion | null => {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return null;

  const downScaledWidth = Math.min(
    attemptCount % FAST_QR_WORKER_HIGH_RES_SCAN_INTERVAL === 0
      ? FAST_QR_WORKER_HIGH_RES_FULL_FRAME_WIDTH
      : FAST_QR_WORKER_FULL_FRAME_WIDTH,
    videoWidth
  );

  return {
    x: 0,
    y: 0,
    width: videoWidth,
    height: videoHeight,
    downScaledWidth,
    downScaledHeight: Math.round((downScaledWidth * videoHeight) / videoWidth),
  };
};

type QrScannerEnginePromise = ReturnType<typeof QrScanner.createQrEngine>;

const closeQrScannerEngine = (
  enginePromise?: QrScannerEnginePromise | null
) => {
  enginePromise
    ?.then((engine) => {
      if (typeof Worker !== "undefined" && engine instanceof Worker) {
        engine.terminate();
      }
    })
    .catch(() => undefined);
};

type VideoElementWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

export const isExpectedQrScanError = (error?: Error | null) => {
  if (!error) return false;

  return ["NotFoundException", "ChecksumException", "FormatException"].some(
    (expectedErrorName) =>
      error.name === expectedErrorName ||
      error.message.includes(expectedErrorName)
  );
};

const NativeQrReader: React.FC<
  FastQrReaderProps & { onUnsupported: () => void }
> = ({
  constraints = FAST_QR_CAMERA_CONSTRAINTS,
  onResult,
  onUnsupported,
  ViewFinder,
  scanDelay = FAST_QR_NATIVE_SCAN_DELAY_MS,
  scanSuccessDelay = FAST_QR_NATIVE_SCAN_SUCCESS_DELAY_MS,
  videoId = "video",
  className,
  containerStyle,
  videoContainerStyle,
  videoStyle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onResultRef = useRef(onResult);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    const BarcodeDetector = getBarcodeDetector();

    if (!BarcodeDetector) {
      onUnsupported();
      return;
    }

    let detector: NativeBarcodeDetector;
    try {
      detector = new BarcodeDetector({ formats: [QR_CODE_FORMAT] });
    } catch (error) {
      onUnsupported();
      return;
    }

    let stopped = false;
    let videoElement: HTMLVideoElement | null = null;
    let timeoutId: number | undefined;
    let attemptCount = 0;
    let lastAttemptLogAt = 0;
    const cameraSession = createRetainedCameraSession(constraints);

    debugQrScanner("native:start", {
      constraints,
      scanDelay,
      scanSuccessDelay,
    });

    async function scan() {
      const video = videoRef.current;
      if (stopped || !video) return;

      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        timeoutId = window.setTimeout(scan, scanDelay);
        return;
      }

      attemptCount += 1;
      const attemptStartedAt = getTimestamp();

      if (attemptStartedAt - lastAttemptLogAt > 1000) {
        lastAttemptLogAt = attemptStartedAt;
        debugQrScanner("native:scanning-full-frame", {
          attemptCount,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        });
      }

      try {
        const barcodes = await detector.detect(video);
        const qrCode = barcodes.find(
          (barcode) => !barcode.format || barcode.format === QR_CODE_FORMAT
        );
        const rawValue = qrCode?.rawValue;

        if (rawValue) {
          debugQrScanner("native:success", {
            attemptCount,
            detectMs: Math.round(getTimestamp() - attemptStartedAt),
            value: formatQrValueForLog(rawValue),
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
          });
          onResultRef.current?.(createNativeQrResult(rawValue), undefined);
          timeoutId = window.setTimeout(scan, scanSuccessDelay);
        } else {
          timeoutId = window.setTimeout(scan, scanDelay);
        }
      } catch (error) {
        debugQrScanner("native:error", {
          message: (error as Error).message,
        });
        onResultRef.current?.(null, error as Error);
        onUnsupported();
      }
    }

    const start = async () => {
      try {
        const stream = await cameraSession.acquire();
        if (!stream) return;

        const video = videoRef.current;
        videoElement = video;
        if (stopped || !video) {
          return;
        }

        video.srcObject = stream;
        await video.play();
        await waitForVideoReady(video);
        timeoutId = window.setTimeout(scan, 0);
      } catch (error) {
        debugQrScanner("native:start-error", {
          message: (error as Error).message,
        });
        onResultRef.current?.(null, error as Error);
        onUnsupported();
      }
    };

    start();

    return () => {
      stopped = true;
      window.clearTimeout(timeoutId);
      cameraSession.release();
      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
      }
    };
  }, [constraints, onUnsupported, scanDelay, scanSuccessDelay]);

  const shouldMirrorUserCamera = getFacingMode(constraints) === "user";

  return (
    <section className={className} style={containerStyle}>
      <div
        style={{
          ...defaultVideoContainerStyle,
          ...videoContainerStyle,
        }}
      >
        {!!ViewFinder && <ViewFinder />}
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          id={videoId}
          style={{
            ...defaultVideoStyle,
            ...videoStyle,
            transform: shouldMirrorUserCamera
              ? "scaleX(-1)"
              : videoStyle?.transform,
          }}
        />
      </div>
    </section>
  );
};

const QrScannerWorkerReader: React.FC<
  FastQrReaderProps & { onUnsupported: () => void }
> = ({
  constraints = FAST_QR_CAMERA_CONSTRAINTS,
  onResult,
  onUnsupported,
  ViewFinder,
  scanDelay = FAST_QR_WORKER_SCAN_DELAY_MS,
  scanSuccessDelay = FAST_QR_WORKER_SCAN_DELAY_MS,
  workerRequiredSuccessfulScans = FAST_QR_WORKER_REQUIRED_SUCCESSFUL_SCANS,
  videoId = "video",
  className,
  containerStyle,
  videoContainerStyle,
  videoStyle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onResultRef = useRef(onResult);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    let stopped = false;
    let videoElement: HTMLVideoElement | null = null;
    let timeoutId: number | undefined;
    let animationFrameId: number | undefined;
    let videoFrameCallbackId: number | undefined;
    let attemptCount = 0;
    let lastAttemptLogAt = 0;
    let enginePromise: QrScannerEnginePromise | null = null;
    let pendingResultCount = 0;
    let pendingResultValue = "";
    const scheduleScanRef: { current: (delayMs: number) => void } = {
      current: () => undefined,
    };
    const cameraSession = createRetainedCameraSession(constraints);

    const clearScheduledScan = () => {
      window.clearTimeout(timeoutId);
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (videoFrameCallbackId !== undefined && videoElement) {
        (
          videoElement as VideoElementWithFrameCallback
        ).cancelVideoFrameCallback?.(videoFrameCallbackId);
      }
    };

    const scan = async () => {
      const video = videoRef.current;
      if (stopped || !video || !enginePromise) return;

      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        scheduleScanRef.current(scanDelay);
        return;
      }

      attemptCount += 1;
      const attemptStartedAt = getTimestamp();
      const scanRegion = getQrScannerScanRegion(video, attemptCount);
      const isHighResScan =
        attemptCount % FAST_QR_WORKER_HIGH_RES_SCAN_INTERVAL === 0;

      if (attemptStartedAt - lastAttemptLogAt > 1000) {
        lastAttemptLogAt = attemptStartedAt;
        debugQrScanner("qr-scanner:scanning-full-frame", {
          attemptCount,
          highResScan: isHighResScan,
          scanDelay,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          scanRegion,
        });
      }

      try {
        const result = await QrScanner.scanImage(video, {
          canvas: canvasRef.current,
          qrEngine: enginePromise,
          returnDetailedScanResult: true,
          scanRegion,
        });

        if (result.data === pendingResultValue) {
          pendingResultCount += 1;
        } else {
          pendingResultValue = result.data;
          pendingResultCount = 1;
        }

        if (pendingResultCount < workerRequiredSuccessfulScans) {
          debugQrScanner("qr-scanner:confirming-result", {
            attemptCount,
            confirmations: pendingResultCount,
            requiredConfirmations: workerRequiredSuccessfulScans,
            value: formatQrValueForLog(result.data),
          });
          scheduleScanRef.current(scanDelay);
          return;
        }

        pendingResultCount = 0;
        pendingResultValue = "";

        debugQrScanner("qr-scanner:success", {
          attemptCount,
          detectMs: Math.round(getTimestamp() - attemptStartedAt),
          highResScan: isHighResScan,
          value: formatQrValueForLog(result.data),
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        });
        onResultRef.current?.(createNativeQrResult(result.data), undefined);
        scheduleScanRef.current(scanSuccessDelay);
      } catch (error) {
        const message = getErrorMessage(error);

        if (message === QrScanner.NO_QR_CODE_FOUND) {
          pendingResultCount = 0;
          pendingResultValue = "";
          scheduleScanRef.current(scanDelay);
          return;
        }

        debugQrScanner("qr-scanner:error", {
          message,
        });
        onResultRef.current?.(null, toError(error));
        onUnsupported();
      }
    };

    const scheduleScan = (delayMs: number) => {
      if (stopped) return;

      timeoutId = window.setTimeout(() => {
        if (stopped) return;

        const video = videoRef.current as VideoElementWithFrameCallback | null;
        if (video?.requestVideoFrameCallback) {
          videoFrameCallbackId = video.requestVideoFrameCallback(scan);
        } else {
          animationFrameId = window.requestAnimationFrame(scan);
        }
      }, delayMs);
    };
    scheduleScanRef.current = scheduleScan;

    const start = async () => {
      try {
        debugQrScanner("qr-scanner:start", {
          constraints,
          highResScanInterval: FAST_QR_WORKER_HIGH_RES_SCAN_INTERVAL,
          scanDelay,
          scanSuccessDelay,
          workerRequiredSuccessfulScans,
        });
        enginePromise = QrScanner.createQrEngine();
        canvasRef.current = document.createElement("canvas");

        const stream = await cameraSession.acquire();
        if (!stream) return;

        const video = videoRef.current;
        videoElement = video;
        if (stopped || !video) {
          return;
        }

        video.srcObject = stream;
        await video.play();
        await waitForVideoReady(video);
        scheduleScan(0);
      } catch (error) {
        debugQrScanner("qr-scanner:start-error", {
          message: getErrorMessage(error),
        });
        onResultRef.current?.(null, toError(error));
        onUnsupported();
      }
    };

    start();

    return () => {
      stopped = true;
      clearScheduledScan();
      closeQrScannerEngine(enginePromise);
      cameraSession.release();
      canvasRef.current = null;
      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
      }
    };
  }, [
    constraints,
    onUnsupported,
    scanDelay,
    scanSuccessDelay,
    workerRequiredSuccessfulScans,
  ]);

  const shouldMirrorUserCamera = getFacingMode(constraints) === "user";

  return (
    <section className={className} style={containerStyle}>
      <div
        style={{
          ...defaultVideoContainerStyle,
          ...videoContainerStyle,
        }}
      >
        {!!ViewFinder && <ViewFinder />}
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          id={videoId}
          style={{
            ...defaultVideoStyle,
            ...videoStyle,
            transform: shouldMirrorUserCamera
              ? "scaleX(-1)"
              : videoStyle?.transform,
          }}
        />
      </div>
    </section>
  );
};

const ZxingQrReader: React.FC<FastQrReaderProps> = ({
  constraints = FAST_QR_CAMERA_CONSTRAINTS,
  onResult,
  ViewFinder,
  scanDelay = FAST_QR_ZXING_SCAN_DELAY_MS,
  videoId = "video",
  className,
  containerStyle,
  videoContainerStyle,
  videoStyle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onResultRef = useRef(onResult);
  const fallbackAttemptCountRef = useRef(0);
  const fallbackLastAttemptLogAtRef = useRef(0);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    let stopped = false;
    let controls: IScannerControls | null = null;
    let videoElement: HTMLVideoElement | null = null;
    const codeReader = new BrowserQRCodeReader(undefined, {
      delayBetweenScanAttempts: scanDelay,
      delayBetweenScanSuccess: scanDelay,
    });
    const cameraSession = createRetainedCameraSession(constraints);

    const handleScanResult: OnResultFunction = (result, error, codeReader) => {
      fallbackAttemptCountRef.current += 1;
      const now = getTimestamp();

      if (now - fallbackLastAttemptLogAtRef.current > 1000) {
        fallbackLastAttemptLogAtRef.current = now;
        debugQrScanner("zxing:scanning-full-frame", {
          attemptCount: fallbackAttemptCountRef.current,
          scanDelay,
        });
      }

      if (result) {
        debugQrScanner("zxing:success", {
          attemptCount: fallbackAttemptCountRef.current,
          value: formatQrValueForLog(result.getText()),
        });
      } else if (error && !isExpectedQrScanError(error)) {
        debugQrScanner("zxing:error", {
          message: error.message,
        });
      }

      if (result || !isExpectedQrScanError(error)) {
        onResultRef.current?.(result, error, codeReader);
      }
    };

    const start = async () => {
      try {
        debugQrScanner("zxing:start", { constraints, scanDelay });
        const stream = await cameraSession.acquire();
        if (!stream) return;

        const video = videoRef.current;
        videoElement = video;

        if (stopped || !video) return;

        video.srcObject = stream;
        await video.play();
        await waitForVideoReady(video);

        controls = codeReader.scan(video, (result, error) => {
          handleScanResult(result, error as Error, codeReader);
        });
      } catch (error) {
        debugQrScanner("zxing:start-error", {
          message: (error as Error).message,
        });
        onResultRef.current?.(null, error as Error);
      }
    };

    start();

    return () => {
      stopped = true;
      controls?.stop();
      cameraSession.release();
      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
      }
    };
  }, [constraints, scanDelay]);

  const shouldMirrorUserCamera = getFacingMode(constraints) === "user";

  return (
    <section className={className} style={containerStyle}>
      <div
        style={{
          ...defaultVideoContainerStyle,
          ...videoContainerStyle,
        }}
      >
        {!!ViewFinder && <ViewFinder />}
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          id={videoId}
          style={{
            ...defaultVideoStyle,
            ...videoStyle,
            transform: shouldMirrorUserCamera
              ? "scaleX(-1)"
              : videoStyle?.transform,
          }}
        />
      </div>
    </section>
  );
};

const FastQrReader: React.FC<FastQrReaderProps> = ({
  constraints = FAST_QR_CAMERA_CONSTRAINTS,
  onResult,
  duplicateScanDelay = FAST_QR_DUPLICATE_SCAN_SUPPRESSION_MS,
  scanDelay = FAST_QR_WORKER_SCAN_DELAY_MS,
  nativeScanDelay = FAST_QR_NATIVE_SCAN_DELAY_MS,
  scanSuccessDelay = FAST_QR_NATIVE_SCAN_SUCCESS_DELAY_MS,
  workerRequiredSuccessfulScans = FAST_QR_WORKER_REQUIRED_SUCCESSFUL_SCANS,
  ...props
}) => {
  const [activeEngine, setActiveEngine] = useState<FastQrEngine>(() =>
    getBarcodeDetector() ? "native" : "qr-scanner"
  );

  const qrScannerScanDelay =
    scanDelay === FAST_QR_ZXING_SCAN_DELAY_MS
      ? FAST_QR_WORKER_SCAN_DELAY_MS
      : scanDelay;

  const zxingScanDelay =
    scanDelay === FAST_QR_WORKER_SCAN_DELAY_MS
      ? FAST_QR_ZXING_SCAN_DELAY_MS
      : scanDelay;

  const qrScannerSuccessDelay =
    scanSuccessDelay === FAST_QR_NATIVE_SCAN_SUCCESS_DELAY_MS
      ? FAST_QR_WORKER_SCAN_DELAY_MS
      : scanSuccessDelay;
  const duplicateSuppressedOnResult = useDuplicateSuppressedOnResult({
    activeEngine,
    duplicateScanDelay,
    onResult,
  });

  useEffect(() => {
    debugQrScanner(`engine:${activeEngine}`, {
      constraints,
      duplicateScanDelay,
      nativeScanDelay,
      qrScannerScanDelay,
      qrScannerSuccessDelay,
      workerRequiredSuccessfulScans,
      zxingScanDelay,
    });
  }, [
    activeEngine,
    constraints,
    duplicateScanDelay,
    nativeScanDelay,
    qrScannerScanDelay,
    qrScannerSuccessDelay,
    workerRequiredSuccessfulScans,
    zxingScanDelay,
  ]);

  useEffect(() => {
    if (activeEngine !== "native") return;

    const BarcodeDetector = getBarcodeDetector();
    if (!BarcodeDetector?.getSupportedFormats) return;

    let isMounted = true;

    BarcodeDetector.getSupportedFormats()
      .then((formats) => {
        if (isMounted && !formats.includes(QR_CODE_FORMAT)) {
          debugQrScanner("native:unsupported-format", { formats });
          setActiveEngine("qr-scanner");
        }
      })
      .catch(() => {
        debugQrScanner("native:supported-formats-error");
        if (isMounted) setActiveEngine("qr-scanner");
      });

    return () => {
      isMounted = false;
    };
  }, [activeEngine]);

  const useQrScannerFallback = useCallback(() => {
    debugQrScanner("native:unsupported");
    setActiveEngine("qr-scanner");
  }, []);

  const useZxingFallback = useCallback(() => {
    debugQrScanner("qr-scanner:unsupported");
    setActiveEngine("zxing");
  }, []);

  if (activeEngine === "native") {
    return (
      <NativeQrReader
        {...props}
        constraints={constraints}
        onResult={duplicateSuppressedOnResult}
        onUnsupported={useQrScannerFallback}
        scanDelay={nativeScanDelay}
        scanSuccessDelay={scanSuccessDelay}
      />
    );
  }

  if (activeEngine === "qr-scanner") {
    return (
      <QrScannerWorkerReader
        {...props}
        constraints={constraints}
        onResult={duplicateSuppressedOnResult}
        onUnsupported={useZxingFallback}
        scanDelay={qrScannerScanDelay}
        scanSuccessDelay={qrScannerSuccessDelay}
        workerRequiredSuccessfulScans={workerRequiredSuccessfulScans}
      />
    );
  }

  return (
    <ZxingQrReader
      {...props}
      constraints={constraints}
      onResult={duplicateSuppressedOnResult}
      scanDelay={zxingScanDelay}
    />
  );
};

export default FastQrReader;
